import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import labExtractionService, { type ExtractionJob, type LabTest } from '@/services/labExtractionService';
import {
  ArrowLeft,
  Check,
  X,
  Save,
  AlertCircle,
  FileText,
  Calendar,
  Building2,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';

const LabExtractionReviewSheet = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [job, setJob] = useState<ExtractionJob | null>(null);
  const [editedTests, setEditedTests] = useState<LabTest[]>([]);
  const [editedProvider, setEditedProvider] = useState('');
  const [editedCollectionDate, setEditedCollectionDate] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (jobId) {
      fetchJob();
    }
  }, [jobId]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const data = await labExtractionService.getExtractionJobById(jobId!);
      setJob(data);
      setEditedTests(data.extractionResult?.tests || []);
      setEditedProvider(data.detectedProvider || '');
      setEditedCollectionDate(data.detectedCollectionDate || '');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load extraction job',
        variant: 'destructive',
      });
      navigate('/clinic/labs/extractions');
    } finally {
      setLoading(false);
    }
  };

  const handleTestChange = (index: number, field: keyof LabTest, value: any) => {
    const updated = [...editedTests];
    updated[index] = { ...updated[index], [field]: value };
    setEditedTests(updated);
    setHasUnsavedChanges(true);
  };

  const handleDeleteTest = (index: number) => {
    const updated = editedTests.filter((_, i) => i !== index);
    setEditedTests(updated);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!jobId) return;
    
    try {
      setSaving(true);
      const updatedJob = await labExtractionService.updateExtractionResults(jobId, {
        tests: editedTests,
        detectedProvider: editedProvider,
        detectedCollectionDate: editedCollectionDate,
      });
      setJob(updatedJob);
      setHasUnsavedChanges(false);
      toast({
        title: 'Saved',
        description: 'Changes saved successfully',
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save changes',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!jobId) return;

    // Save changes first if needed
    if (hasUnsavedChanges) {
      await handleSave();
    }

    try {
      setSaving(true);
      await labExtractionService.approveExtractionJob(jobId);
      toast({
        title: 'Approved',
        description: 'Lab results approved and saved to patient record',
        variant: 'success',
      });
      navigate('/clinic/labs/extractions');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve extraction',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
      setShowApproveDialog(false);
    }
  };

  const handleReject = async () => {
    if (!jobId) return;

    try {
      setSaving(true);
      await labExtractionService.rejectExtractionJob(jobId, rejectReason);
      toast({
        title: 'Rejected',
        description: 'Extraction job rejected',
        variant: 'default',
      });
      navigate('/clinic/labs/extractions');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject extraction',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
      setShowRejectDialog(false);
    }
  };

  const getConfidenceBadge = (confidence: number | undefined) => {
    if (!confidence) return null;
    const percentage = Math.round(confidence * 100);
    let color = 'bg-emerald-500';
    
    if (confidence >= 0.8) {
      color = 'bg-emerald-500';
    } else if (confidence >= 0.6) {
      color = 'bg-amber-500';
    } else {
      color = 'bg-red-500';
    }

    return (
      <Badge className={`${color} text-white gap-1`}>
        <div className="w-2 h-2 rounded-full bg-white" />
        {percentage}%
      </Badge>
    );
  };

  const getStatusBadge = (status: string | undefined) => {
    const variants: Record<string, { label: string; color: string }> = {
      normal: { label: 'Normal', color: 'bg-emerald-500' },
      elevated: { label: 'Elevated', color: 'bg-red-500' },
      high: { label: 'High', color: 'bg-red-500' },
      low: { label: 'Low', color: 'bg-amber-500' },
      critical: { label: 'Critical', color: 'bg-red-600' },
      abnormal: { label: 'Abnormal', color: 'bg-amber-600' },
    };
    const config = status ? variants[status] : null;
    if (!config) return <span className="text-slate-400">—</span>;
    
    return (
      <Badge className={`${config.color} text-white`}>
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex items-center justify-center h-screen text-slate-500">
        <p>Extraction job not found</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/clinic/labs/extractions')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {job.patient.firstName} {job.patient.lastName}
            </h1>
            <p className="text-sm text-slate-600">{job.fileName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <Badge variant="outline" className="text-amber-600 border-amber-600">
              Unsaved Changes
            </Badge>
          )}
          <Button
            onClick={handleSave}
            disabled={!hasUnsavedChanges || saving}
            variant="outline"
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            Save
          </Button>
          <Button
            onClick={() => setShowRejectDialog(true)}
            disabled={saving}
            variant="outline"
            className="gap-2 text-red-600 hover:bg-red-50"
          >
            <X className="h-4 w-4" />
            Reject
          </Button>
          <Button
            onClick={() => setShowApproveDialog(true)}
            disabled={saving || hasUnsavedChanges}
            className="gap-2 bg-teal-600 hover:bg-teal-700"
          >
            <Check className="h-4 w-4" />
            Approve
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* PDF Viewer (Left) */}
        <div className="w-1/2 border-r bg-white p-6 overflow-auto">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-600" />
              Original Document
            </h2>
            {job.presignedUrl ? (
              <div className="border rounded-lg overflow-hidden">
                {job.fileType === 'application/pdf' ? (
                  <iframe
                    src={job.presignedUrl}
                    className="w-full h-[calc(100vh-200px)]"
                    title="Lab Report PDF"
                  />
                ) : (
                  <img
                    src={job.presignedUrl}
                    alt="Lab Report"
                    className="w-full"
                  />
                )}
              </div>
            ) : (
              <div className="border rounded-lg p-12 text-center text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>Document preview not available</p>
              </div>
            )}
          </div>
        </div>

        {/* Extracted Data (Right) */}
        <div className="w-1/2 p-6 overflow-auto">
          <div className="space-y-6">
            {/* Metadata Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Document Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Lab Provider
                  </label>
                  <Input
                    value={editedProvider}
                    onChange={(e) => {
                      setEditedProvider(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    placeholder="e.g., Quest Diagnostics"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Collection Date
                  </label>
                  <Input
                    type="date"
                    value={editedCollectionDate}
                    onChange={(e) => {
                      setEditedCollectionDate(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-700">Extraction Confidence:</span>
                  {getConfidenceBadge(job.extractionConfidence || undefined)}
                </div>
                {job.hasDuplicates && (
                  <div className="flex items-center gap-2 text-amber-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">Potential duplicate tests detected</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tests Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Lab Results ({editedTests.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Test Name</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Reference Range</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Confidence</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editedTests.map((test, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Input
                              value={test.test_name}
                              onChange={(e) => handleTestChange(index, 'test_name', e.target.value)}
                              className="min-w-[200px]"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={test.value || ''}
                              onChange={(e) => handleTestChange(index, 'value', parseFloat(e.target.value))}
                              className="w-24"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={test.unit}
                              onChange={(e) => handleTestChange(index, 'unit', e.target.value)}
                              className="w-24"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={test.reference_range || ''}
                              onChange={(e) => handleTestChange(index, 'reference_range', e.target.value)}
                              className="w-32"
                            />
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(test.status || undefined)}
                          </TableCell>
                          <TableCell>
                            {getConfidenceBadge(test.confidence)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTest(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Lab Results?</DialogTitle>
            <DialogDescription>
              This will save the extracted lab results to the patient's record. Make sure all data has been reviewed and is accurate.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? 'Approving...' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Extraction?</DialogTitle>
            <DialogDescription>
              This will reject the extraction job. The lab results will not be saved to the patient's record.
            </DialogDescription>
          </DialogHeader>
          <div className="my-4">
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Reason for rejection (optional)
            </label>
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., Poor image quality, incorrect extraction"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={saving}
              variant="destructive"
            >
              {saving ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabExtractionReviewSheet;
